/**
 * 홈 화면 설치는 이용자의 클릭으로만 진행한다.
 * 2026-09-05 사용자 요청으로 접속 후 작은 비차단 안내를 추가했다.
 * 첫 10초·시나리오·다른 패널을 방해하지 않는 시점은 install-offer가 맡는다.
 *
 * 브라우저마다 다른 것:
 *   · 안드로이드 크롬 — `beforeinstallprompt` 를 준다. 이걸 잡아뒀다가 사용자가
 *     누를 때 `prompt()` 한다. **잡아두지 않으면 기회가 그냥 지나간다**(1회성 이벤트).
 *   · iOS 사파리 — 그런 이벤트가 없다. **공유 → 홈 화면에 추가** 를 사람이 직접 해야 하므로
 *     그 경로를 글로 알려주는 것 외에 방법이 없다. 없는 버튼을 누르라고 하면 안 된다.
 *   · 이미 홈 화면에서 연 경우 — 권할 이유가 없다. 아예 안 보여준다.
 */
import { S } from '../core/state.js';

/** 안드로이드가 준 설치 기회. 1회성이라 잡아두지 않으면 사라진다 */
let deferred = null, installed = false, initialized = false;

/** 홈 화면(독립 창)에서 열렸나 */
export function standalone() {
  try {
    return matchMedia('(display-mode: standalone)').matches
      || matchMedia('(display-mode: fullscreen)').matches
      || navigator.standalone === true; // iOS 는 이 비표준 값만 준다
  } catch { return false; }
}

const iOS = () => /iP(hone|ad|od)/.test(navigator.userAgent)
  // 아이패드는 iPadOS 13 부터 자기를 Mac 이라고 말한다. 터치가 있는 Mac 은 아이패드다.
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/**
 * 지금 이 사람에게 무엇을 보여줘야 하나.
 *   'button' 안드로이드 — 누르면 바로 설치된다
 *   'ios'    사파리 — 손으로 하는 길을 알려준다
 *   null     이미 홈 화면이거나, 브라우저가 설치를 지원하지 않는다
 */
export function installMode() {
  if (installed || standalone()) return null;
  if (deferred) return 'button';
  if (iOS()) return 'ios';
  return null;
}

/** 사용자가 눌렀을 때만 부른다. 자동으로 부르면 브라우저가 무시한다 */
export async function promptInstall() {
  if (!deferred) return false;
  const e = deferred;
  deferred = null; S.canInstall = false; // 실패·거절이어도 이 이벤트는 다시 못 쓴다
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome === 'accepted';
  } catch { return false; }
}

/**
 * 서비스워커를 등록한다 — **설치로 대접받기 위한 최소 조건**이다.
 * 캐시는 하지 않는다(`public/sw.js` 주석 참조). 등록에 실패해도 게임은 그대로 돈다.
 */
export function initInstall() {
  if (initialized) return; initialized = true;
  addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();      // 브라우저 기본 배너를 막고
    deferred = e;            // 우리 버튼이 쓸 수 있게 잡아둔다
    S.canInstall = true;
  });
  addEventListener('appinstalled', () => { installed = true; deferred = null; S.canInstall = false; });

  if ('serviceWorker' in navigator) {
    // 로드가 끝난 뒤에 등록한다 — 첫 화면이 뜨는 것보다 먼저일 이유가 없다
    addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* 실패해도 게임은 돈다 */ });
    });
  }
}
